import {z} from "zod";
const id = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "Enter a valid UUID");
const time = z.string().datetime({offset: true});
const count = z.number().int().nonnegative();
export const healthSearchValidator = z
    .object({
        windowMinutes: z.coerce
            .number()
            .refine((value) => [30, 60, 300, 1440, 10080].includes(value))
            .default(1440),
        from: time.optional(),
        to: time.optional(),
        companyId: id.optional(),
        dataSourceTemplateId: id.optional(),
        dataSourceId: id.optional(),
        dataClusterTemplateId: id.optional(),
        dataClusterId: id.optional(),
        cursorType: z.enum(["INITIAL", "FUTURE", "HISTORICAL"]).optional(),
        state: z.enum(["COMPLETED", "FAILED", "SUPERSEDED", "DISCARDED"]).optional(),
        taskType: z.enum(["SYNC", "ALL", "INITIALIZE", "REFRESH", "DELETE"]).default("SYNC"),
        taskId: id.optional(),
        errorSearch: z.string().max(200).optional(),
        page: z.coerce.number().int().min(1).max(1000000).default(1),
    })
    .superRefine((value, context) => {
        if (value.from === undefined && value.to === undefined) {
            return;
        }
        if (value.from === undefined || value.to === undefined) {
            context.addIssue({code: "custom", message: "Choose both custom start and end times", path: ["to"]});
            return;
        }
        const duration = Date.parse(value.to) - Date.parse(value.from);
        if (duration <= 0 || duration > 7 * 86400000) {
            context.addIssue({code: "custom", message: "Choose an increasing time range of at most 7 days", path: ["to"]});
        }
    });
export type HealthSearch = z.infer<typeof healthSearchValidator>;
export type HealthEndpoint = "summary" | "breakdowns" | "attempts" | "queue";
export function requestBody(filters: HealthSearch, endpoint: HealthEndpoint): Record<string, unknown> {
    const {companyId, dataSourceTemplateId, dataSourceId, dataClusterTemplateId, dataClusterId, cursorType} = filters;
    const body: Record<string, unknown> = {companyId, dataSourceTemplateId, dataSourceId, dataClusterTemplateId, dataClusterId, cursorType, debugMode: "EXCLUDE"};
    if (endpoint !== "queue") {
        Object.assign(body, {windowMinutes: filters.windowMinutes, from: filters.from, to: filters.to});
    }
    if (endpoint === "queue" || endpoint === "attempts") {
        body.taskType = filters.taskType;
    }
    if (endpoint === "attempts") {
        Object.assign(body, {state: filters.state, taskId: filters.taskId, errorSearch: filters.errorSearch, limit: 25, offset: (filters.page - 1) * 25});
    }
    return body;
}
export function changeScope(
    filters: HealthSearch,
    key: "companyId" | "dataSourceTemplateId" | "dataClusterTemplateId" | "dataSourceId" | "dataClusterId",
    value?: string,
): HealthSearch {
    const next = {...filters, [key]: value, page: 1};
    if (key === "companyId" || key === "dataSourceTemplateId") {
        next.dataSourceId = undefined;
        next.dataClusterId = undefined;
    }
    if (key === "dataSourceTemplateId") {
        next.dataClusterTemplateId = undefined;
    }
    if (key === "dataClusterTemplateId" || key === "dataSourceId") {
        next.dataClusterId = undefined;
    }
    return next;
}
export const durationValidator = z.object({samples: count, averageMilliseconds: z.number().nullable(), p95Milliseconds: z.number().nullable()});
export const bucketValidator = z.object({at: time, completed: count, failed: count, processing: durationValidator, queueWait: durationValidator});
export const summaryValidator = z.object({
    asOf: time,
    from: time,
    to: time,
    bucketSeconds: count,
    completed: count,
    failed: count,
    distinctTasks: count,
    affectedCompanies: count,
    affectedClusters: count,
    superseded: count,
    discarded: count,
    unclassified: count,
    successRate: z.number().min(0).max(100).nullable(),
    processing: durationValidator,
    queueWait: durationValidator,
    buckets: z.array(bucketValidator),
});
export const dimensionValidator = z.object({
    id: id.nullable(),
    name: z.string().nullable(),
    completed: count,
    failed: count,
    lastSuccessAt: time.nullable(),
    latestState: z.string().nullable(),
});
export const breakdownsValidator = z.object({
    asOf: time,
    reasons: z.array(z.object({message: z.string().nullable(), failures: count})),
    companies: z.array(dimensionValidator),
    sources: z.array(dimensionValidator),
    templates: z.array(dimensionValidator),
});
export const attemptValidator = z.object({
    id,
    taskId: id,
    companyId: id.nullable(),
    dataClusterId: id.nullable(),
    dataClusterTemplateId: id.nullable(),
    dataSourceTemplateId: id.nullable(),
    dataSourceId: id.nullable(),
    dataClusterName: z.string().nullable(),
    templateName: z.string().nullable(),
    sourceName: z.string().nullable(),
    accountName: z.string().nullable(),
    taskState: z.string(),
    taskType: z.string().nullable(),
    cursorType: z.string().nullable(),
    debugging: z.boolean(),
    attemptNumber: count,
    recordedAt: time.nullable(),
    taskCreatedAt: time.nullable(),
    scheduledAt: time.nullable(),
    startedAt: time.nullable(),
    finishedAt: time.nullable(),
    errorMessage: z.string().nullable(),
    processingMilliseconds: z.number().nullable(),
    queueWaitMilliseconds: z.number().nullable(),
    totalWaitMilliseconds: z.number().nullable(),
    rowsInserted: z.number(),
    rowsDeleted: z.number(),
    recoveryEvidence: z.string().nullable(),
    laterSuccessAt: time.nullable(),
});
export const attemptsValidator = z.object({items: z.array(attemptValidator), total: count, limit: count, offset: count, asOf: time, from: time, to: time});
export const queueValidator = z.object({
    queued: count,
    due: count,
    scheduled: count,
    inProgress: count,
    staleClaims: count,
    blocked: count,
    oldestEligibleWaitMilliseconds: z.number().nullable(),
    asOf: time,
});
const pool = z.object({capacity: count, active: count, available: count, completedSinceStart: count});
export const workersValidator = z.object({
    instanceId: z.string(),
    workersAvailable: z.boolean(),
    lightPool: pool.nullable(),
    heavyPool: pool.nullable(),
    asOf: time,
});
export const healthValidators = {
    summary: summaryValidator,
    breakdowns: breakdownsValidator,
    attempts: attemptsValidator,
    queue: queueValidator,
    workers: workersValidator,
};
export type Summary = z.infer<typeof summaryValidator>;
export type Breakdowns = z.infer<typeof breakdownsValidator>;
export type Attempt = z.infer<typeof attemptValidator>;
export {formatDuration} from "../ottosync-task-manager/-model.ts";

export function catalogPickerRequest(
    scope: HealthSearch,
    endpoint: "sources" | "accounts" | "templates" | "instances",
    search: string,
    page: number,
): Record<string, unknown> {
    const filter: Record<string, unknown> = {companyId: scope.companyId, search, limit: 25, offset: (page - 1) * 25};
    if (endpoint !== "sources") {
        filter.dataSourceTemplateId = scope.dataSourceTemplateId;
    }
    if (endpoint === "instances") {
        Object.assign(filter, {dataSourceId: scope.dataSourceId, dataClusterTemplateId: scope.dataClusterTemplateId});
    }
    return filter;
}
